import {
    useEffect,
    useMemo,
    useState,
} from 'react'

import {
    noop,
} from 'lodash'
import {
    FormPart,
} from 'app/types/form.types'
import {
    DataUpdateForm,
    UploadFeedback,
    ApiDataLoader,
    CsvDataLoader,
} from './components'
import useStyles from './SimpleBulkUpload.styles'
import useBulkUploadRecords from './hooks/useBulkUploadRecords'
import {
    BulkLoaderType,
    FeedbackFormData,
    SimpleBulkUploadProps, UpdateFormData,
} from './SimpleBulkUpload.types'

/* TODO-JG: When CSV implementation,
 probably split this component into two separate components for API and CSV if UI differs */
const SimpleBulkUpload = <T extends Record<string, any>>({
    entity,
    uniqField = 'id' as Extract<keyof T, string>,
    loaderType = BulkLoaderType.API,
    dataRequest,
    dataRequestKey,
    uploadParamsMapper,
    filterParams,
    dataUpdateFormFields,
    feedbackFormFields,
    errorFeedbackColumns,
    onCancel,
    onUpdateFormChange = noop,
    updateFormValue: externalUpdateFormValue,
    dataUpdateFormInfoComponent,
    mandatoryCsvFields = [],
    classNames = {},
}: Readonly<SimpleBulkUploadProps<T>>) => {
    const {
        classes,
    } = useStyles()

    const [
        data,
        setData,
    ] = useState<Record<string, any>[] | null>(null)

    const defaultUpdateFormValue: UpdateFormData = useMemo(() => {
        return {
            selectedRecords: data?.length,
        }
    }, [data?.length])

    const [
        updateFormValue,
        setUpdateFormValue,
    ] = useState<UpdateFormData>()

    useEffect(() => {
        setUpdateFormValue(defaultUpdateFormValue)
    }, [defaultUpdateFormValue])

    useEffect(() => {
        if (externalUpdateFormValue) {
            setUpdateFormValue(externalUpdateFormValue)
        }
    }, [externalUpdateFormValue])

    const {
        uploadRecords,
        stopUploadRecords,
        failedUploads,
        successfulUploads,
        isLoading: isUploadingRecords,
    } = useBulkUploadRecords({
        uploadParamsMapper,
        plainData: data,
        entity,
    })

    const handleUpdateFormChange = (formData: UpdateFormData) => {
        onUpdateFormChange(formData as UpdateFormData & T)
        setUpdateFormValue(formData)
    }

    // TODO-JG: Move to other component when implementing CSV
    const mandatoryCsvFieldsWithUniqField = useMemo(() => {
        if (mandatoryCsvFields.includes(uniqField)) {
            return mandatoryCsvFields
        }
        return [
            ...mandatoryCsvFields,
            uniqField,
        ]
    }, [
        mandatoryCsvFields,
        uniqField,
    ])

    // TODO-JG: Leave only API loader when implementing CSV
    const loaderMap = useMemo(() => {
        return {
            [BulkLoaderType.API]: <ApiDataLoader
                request={dataRequest}
                requestKey={dataRequestKey}
                filterParams={filterParams}
                onCancel={onCancel}
                onLoadData={setData}
            />,
            [BulkLoaderType.CSV]: <CsvDataLoader
                onLoadData={setData}
                mandatoryFields={mandatoryCsvFieldsWithUniqField}
            />,
        }
    }, [
        dataRequest,
        dataRequestKey,
        filterParams,
        mandatoryCsvFieldsWithUniqField,
        onCancel,
    ])

    const feedbackFormValue: FeedbackFormData = useMemo(() => {
        return {
            selectedRecords: data?.length,
            updatedRecords: successfulUploads,
            ...updateFormValue,
        }
    }, [
        data?.length,
        successfulUploads,
        updateFormValue,
    ])

    const uploadStepVisible = useMemo(() => {
        return failedUploads?.length > 0 || successfulUploads > 0
    }, [
        failedUploads,
        successfulUploads,
    ])

    return (
        <div className={classes.container}>
            {data === null && loaderMap[loaderType]}

            {(data !== null && !uploadStepVisible) && (
                <>
                    {dataUpdateFormInfoComponent}
                    <DataUpdateForm
                        formValue={updateFormValue}
                        formFields={dataUpdateFormFields as FormPart<keyof UpdateFormData>[]}
                        onFormChange={handleUpdateFormChange}
                        onSubmit={uploadRecords}
                        onCancel={onCancel}
                        classNames={classNames.dataUpdateForm}
                    />
                </>
            )}

            {uploadStepVisible && (
                <UploadFeedback
                    uniqField={uniqField}
                    formFields={feedbackFormFields as FormPart<keyof FeedbackFormData>[]}
                    columns={errorFeedbackColumns}
                    failedUploads={failedUploads}
                    feedbackFormValue={feedbackFormValue}
                    isLoading={isUploadingRecords}
                    onStopUpdate={stopUploadRecords}
                    onDoneClick={onCancel}
                />
            )}
        </div>
    )
}

export default SimpleBulkUpload
